import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Bell, CaretDown, FileText, ShieldCheck, Users } from "@phosphor-icons/react";

const REQUIRED_CONSENTS = ["registration"];
const CONSENT_LABELS = {
  registration: "Account creation",
  medical_record_storage: "Medical record storage",
  medical_record_share: "Doctor record access",
  family_data_management: "Family profiles",
  marketing: "Product updates",
};
const SIGNUP_TOKENS = {
  bone: "#FDFBF7",
  forest: "#224032",
  forestSoft: "#3A5A48",
  terracotta: "#C57053",
  terracottaSoft: "#F0DDD3",
  sage: "#E9EEE7",
  ink: "#1B2620",
  inkSoft: "#5C6862",
  line: "#DDD6C8",
};
const SIGNUP_CONSENT_DETAILS = [
  {
    type: "registration",
    icon: ShieldCheck,
    title: "Account & login",
    body: "We use your name, phone, email, and password to create your account, secure login, and manage bookings.",
  },
  {
    type: "medical_record_storage",
    icon: FileText,
    title: "Medical records",
    body: "Uploaded records stay in your account and are shared with doctors only when you choose to share them.",
  },
  {
    type: "family_data_management",
    icon: Users,
    title: "Family profiles",
    body: "You can add family members later and manage their appointments from your account.",
  },
  {
    type: "marketing",
    icon: Bell,
    title: "Product updates",
    body: "Optional updates about new Qenqo features and service improvements.",
  },
];

function dashboardFor(user) {
  if (user?.role === "admin") return "/admin";
  if (user?.role === "doctor") return "/doctor";
  if (user?.role === "staff") return "/staff";
  return "/dashboard";
}

function safeNextPath(value) {
  if (!value || typeof value !== "string") return "";
  if (!value.startsWith("/") || value.startsWith("//")) return "";
  return value;
}

export function LoginForm({ compact = false, onSuccess, onSwitchToRegister }) {
  const { login } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nextParam = safeNextPath(searchParams.get("next"));
  const registerPath = nextParam ? `/register?next=${encodeURIComponent(nextParam)}` : "/register";

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(identifier, password);
      toast.success("Welcome back!");
      onSuccess?.(u);
      const next = safeNextPath(searchParams.get("next")) || safeNextPath(localStorage.getItem("redirectAfterLogin"));
      localStorage.removeItem("redirectAfterLogin");
      nav(next || dashboardFor(u));
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="w-full" data-testid="login-form">
      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Sign in</div>
      <h1 className={`font-display font-bold tracking-tight text-foreground ${compact ? "text-3xl" : "text-3xl sm:text-4xl"}`}>Welcome back</h1>
      <p className="mt-2 text-muted-foreground">Access your bookings, queue status, and records.</p>

      <div className="mt-8 space-y-4">
        <div>
          <Label htmlFor={compact ? "modal-identifier" : "identifier"}>Phone or email</Label>
          <Input
            id={compact ? "modal-identifier" : "identifier"}
            type="text"
            required
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            className="h-12 mt-2 rounded-2xl"
            data-testid="login-identifier"
            autoComplete="email"
          />
        </div>
        <div>
          <Label htmlFor={compact ? "modal-password" : "password"}>Password</Label>
          <PasswordInput
            id={compact ? "modal-password" : "password"}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-12 mt-2 rounded-2xl"
            data-testid="login-password"
            autoComplete="current-password"
          />
        </div>
        <Button type="submit" className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-[#1a3025] mt-2" disabled={busy} data-testid="login-submit">
          {busy ? "Signing in..." : "Sign in"}
        </Button>
      </div>

      <p className="mt-6 text-sm text-muted-foreground text-center">
        <Link to="/forgot-password" className="text-primary underline underline-offset-4">
          Forgot password?
        </Link>
      </p>
      <p className="mt-4 text-sm text-muted-foreground">
        New here?{" "}
        {onSwitchToRegister ? (
          <button type="button" onClick={onSwitchToRegister} className="text-foreground underline underline-offset-4" data-testid="login-to-register-link">
            Create a patient account
          </button>
        ) : (
          <Link to={registerPath} className="text-foreground underline underline-offset-4" data-testid="login-to-register-link">
            Create a patient account
          </Link>
        )}
      </p>
    </form>
  );
}

export function RegisterForm({ compact = false, onSuccess, onSwitchToLogin }) {
  const { register } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [gender, setGender] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyExpanded, setPrivacyExpanded] = useState(false);
  const [consentVersions, setConsentVersions] = useState([]);
  const [consents, setConsents] = useState({
    registration: false,
    medical_record_storage: false,
    medical_record_share: false,
    family_data_management: false,
    marketing: false,
  });
  const [busy, setBusy] = useState(false);
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  useEffect(() => {
    api.get("/consent-versions/current")
      .then(({ data }) => setConsentVersions(data.items || []))
      .catch(() => setConsentVersions([
        { consent_type: "registration", policy_text: "Create and secure your account." },
        { consent_type: "medical_record_storage", policy_text: "Store uploaded medical records for your visits." },
        { consent_type: "medical_record_share", policy_text: "Let selected doctors view shared records." },
        { consent_type: "family_data_management", policy_text: "Manage family member profiles and appointments." },
        { consent_type: "marketing", policy_text: "Receive optional product updates." },
      ]));
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (!gender) {
        toast.error("Please select your gender");
        return;
      }
      if (!otpSent || verificationCode.trim().length !== 6) {
        toast.error("Please enter the 6-digit email verification code");
        return;
      }
      if (!termsAccepted) {
        toast.error("Please accept the Terms & Conditions and Privacy Policy");
        return;
      }
      if (REQUIRED_CONSENTS.some((key) => !consents[key])) {
        toast.error("Please accept all required consent options");
        return;
      }
      const u = await register({
        name,
        mobile_number: phone,
        email,
        password,
        gender,
        verification_code: verificationCode.trim(),
        terms_accepted: termsAccepted,
        consents: Object.entries(consents).filter(([, checked]) => checked).map(([key]) => key),
      });
      toast.success("Account created. Let's book your token.");
      onSuccess?.(u);
      const next = safeNextPath(searchParams.get("next")) || safeNextPath(localStorage.getItem("redirectAfterLogin"));
      localStorage.removeItem("redirectAfterLogin");
      nav(next || "/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  const requestOtp = async () => {
    if (!name.trim()) {
      toast.error("Please enter your full name");
      return;
    }
    if (!phone.trim()) {
      toast.error("Please enter your phone number");
      return;
    }
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }
    if (!gender) {
      toast.error("Please select your gender");
      return;
    }
    setOtpBusy(true);
    try {
      await api.post("/auth/register/request-otp", {
        name: name.trim(),
        mobile_number: phone.trim(),
        email: email.trim(),
        gender,
        role: "patient",
      });
      setOtpSent(true);
      toast.success("Verification code sent to your email");
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not send verification code");
    } finally {
      setOtpBusy(false);
    }
  };

  const fieldPrefix = compact ? "modal-" : "";
  const setRequiredAgreement = (checked) => {
    setTermsAccepted(checked);
    setConsents((prev) => Object.keys(prev).reduce((next, key) => ({ ...next, [key]: checked }), {}));
  };
  const policyTextFor = (type) => consentVersions.find((item) => item.consent_type === type)?.policy_text;

  return (
    <form onSubmit={onSubmit} className="w-full" data-testid="register-form">
      <div className="mb-2 text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: SIGNUP_TOKENS.terracotta }}>Patient sign-up</div>
      <h1 className={`font-display font-bold tracking-tight ${compact ? "text-3xl" : "text-3xl sm:text-4xl"}`} style={{ color: SIGNUP_TOKENS.forest }}>Create your account</h1>
      <p className="mt-2" style={{ color: SIGNUP_TOKENS.inkSoft }}>Reserve tokens, track live queues, and manage your visits.</p>

      <div className="mt-8 space-y-4">
        <div>
          <Label htmlFor={`${fieldPrefix}name`}>Full name</Label>
          <Input id={`${fieldPrefix}name`} required value={name} onChange={(e) => setName(e.target.value)} className="h-12 mt-2 rounded-2xl" data-testid="register-name" />
        </div>
        <div>
          <Label htmlFor={`${fieldPrefix}phone`}>Phone</Label>
          <Input id={`${fieldPrefix}phone`} required value={phone} onChange={(e) => setPhone(e.target.value)} className="h-12 mt-2 rounded-2xl" data-testid="register-phone" />
        </div>
        <div>
          <Label htmlFor={`${fieldPrefix}email`}>Email</Label>
          <Input id={`${fieldPrefix}email`} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 mt-2 rounded-2xl" data-testid="register-email" autoComplete="email" />
        </div>
        <div>
          <Label htmlFor={`${fieldPrefix}gender`}>Gender</Label>
          <select
            id={`${fieldPrefix}gender`}
            required
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            className="h-12 mt-2 w-full rounded-2xl border border-input bg-background px-4 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="register-gender"
          >
            <option value="">Select gender</option>
            <option value="female">Female</option>
            <option value="male">Male</option>
            <option value="other">Other</option>
            <option value="prefer_not_to_say">Prefer not to say</option>
          </select>
        </div>
        {!otpSent ? (
          <Button
            type="button"
            onClick={requestOtp}
            className="h-12 w-full rounded-xl border-0 font-display text-base font-semibold shadow-none sm:col-span-2"
            style={{
              background: otpBusy ? SIGNUP_TOKENS.line : SIGNUP_TOKENS.forest,
              color: otpBusy ? SIGNUP_TOKENS.inkSoft : SIGNUP_TOKENS.bone,
              cursor: otpBusy ? "not-allowed" : "pointer",
            }}
            disabled={otpBusy}
          >
            {otpBusy ? "Sending code..." : "Request OTP"}
          </Button>
        ) : (
          <>
            <div className="rounded-2xl border p-3 text-sm sm:col-span-2" style={{ borderColor: SIGNUP_TOKENS.line, background: SIGNUP_TOKENS.sage, color: SIGNUP_TOKENS.forestSoft }}>
              OTP sent to <span className="font-semibold" style={{ color: SIGNUP_TOKENS.forest }}>{email}</span>.{" "}
              <button type="button" onClick={requestOtp} disabled={otpBusy} className="font-semibold underline underline-offset-4">
                {otpBusy ? "Sending..." : "Resend"}
              </button>
            </div>
            <div>
              <Label htmlFor={`${fieldPrefix}otp`}>Email OTP</Label>
              <Input
                id={`${fieldPrefix}otp`}
                required
                inputMode="numeric"
                maxLength={6}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="h-12 mt-2 rounded-2xl"
                data-testid="register-otp"
                placeholder="6-digit code"
              />
            </div>
            <div>
              <Label htmlFor={`${fieldPrefix}password`}>Password</Label>
              <PasswordInput id={`${fieldPrefix}password`} required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 mt-2 rounded-2xl" data-testid="register-password" autoComplete="new-password" />
            </div>
        <div
          className="overflow-hidden rounded-2xl sm:col-span-2"
          style={{ background: SIGNUP_TOKENS.sage, border: `1px solid ${SIGNUP_TOKENS.line}` }}
        >
          <label className="flex cursor-pointer items-start gap-3 p-4 text-sm" style={{ color: SIGNUP_TOKENS.ink }}>
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setRequiredAgreement(e.target.checked)}
              className="mt-1 h-5 w-5 rounded-md"
              style={{ accentColor: SIGNUP_TOKENS.forest }}
              data-testid="register-terms"
            />
            <span className="leading-relaxed">
              I agree to the{" "}
              <Link to="/terms" className="font-semibold underline underline-offset-4" style={{ color: SIGNUP_TOKENS.forest }}>Terms & Conditions</Link>
              {" "}and{" "}
              <Link to="/privacy" className="font-semibold underline underline-offset-4" style={{ color: SIGNUP_TOKENS.forest }}>Privacy Policy</Link>
              , including required secure data processing for appointments and account access.
            </span>
          </label>

          <button
            type="button"
            onClick={() => setPrivacyExpanded((value) => !value)}
            className="flex w-full items-center justify-between border-t px-4 py-3 text-left text-xs font-semibold transition-colors hover:bg-white/35"
            style={{ borderColor: SIGNUP_TOKENS.line, color: SIGNUP_TOKENS.forestSoft }}
            aria-expanded={privacyExpanded}
          >
            What exactly am I agreeing to?
            <CaretDown size={15} className={`transition-transform ${privacyExpanded ? "rotate-180" : ""}`} />
          </button>

          <div className={`grid transition-[grid-template-rows] duration-300 ${privacyExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
            <div className="overflow-hidden">
              <div className="space-y-3 px-4 pb-4 pt-1">
                {SIGNUP_CONSENT_DETAILS.map(({ type, icon: Icon, title, body }) => {
                  const optional = !REQUIRED_CONSENTS.includes(type);
                  return (
                    <div key={type} className="flex gap-3">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#F0DDD3] text-[#C57053]">
                        <Icon size={16} weight="bold" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="text-sm font-semibold" style={{ color: SIGNUP_TOKENS.forest }}>{title}</div>
                          {optional && (
                            <label className="ml-auto flex items-center gap-1.5 text-[11px] font-semibold" style={{ color: SIGNUP_TOKENS.inkSoft }}>
                              <input
                                type="checkbox"
                                checked={!!consents[type]}
                                onChange={(e) => setConsents((prev) => ({ ...prev, [type]: e.target.checked }))}
                                className="h-3.5 w-3.5 rounded"
                                style={{ accentColor: SIGNUP_TOKENS.forest }}
                              />
                              Optional
                            </label>
                          )}
                        </div>
                        <p className="mt-1 text-xs leading-relaxed" style={{ color: SIGNUP_TOKENS.inkSoft }}>
                          {policyTextFor(type) || body}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div className="border-t border-dashed pt-3 text-xs italic leading-relaxed" style={{ borderColor: SIGNUP_TOKENS.line, color: SIGNUP_TOKENS.inkSoft }}>
                  Doctor-specific medical record sharing is asked separately when you share a record. You can update optional choices anytime from My Data & Privacy.
                </div>
              </div>
            </div>
          </div>
        </div>
        <Button
          type="submit"
          className="mt-2 h-12 w-full rounded-xl border-0 font-display text-base font-semibold shadow-none sm:col-span-2"
          style={{
            background: busy || !termsAccepted ? SIGNUP_TOKENS.line : SIGNUP_TOKENS.forest,
            color: busy || !termsAccepted ? SIGNUP_TOKENS.inkSoft : SIGNUP_TOKENS.bone,
            cursor: busy || !termsAccepted ? "not-allowed" : "pointer",
          }}
          disabled={busy || !termsAccepted}
          data-testid="register-submit"
        >
          {busy ? "Creating account..." : "Create account"}
        </Button>
          </>
        )}
      </div>

      <p className="mt-8 text-sm text-muted-foreground">
        Already have an account?{" "}
        {onSwitchToLogin ? (
          <button type="button" onClick={onSwitchToLogin} className="text-foreground underline underline-offset-4" data-testid="register-to-login-link">
            Sign in
          </button>
        ) : (
          <Link to="/login" className="text-foreground underline underline-offset-4" data-testid="register-to-login-link">
            Sign in
          </Link>
        )}
      </p>
    </form>
  );
}

export function AuthModal({ open, onOpenChange, initialMode = "login" }) {
  const [mode, setMode] = useState(initialMode);

  useEffect(() => {
    if (open) setMode(initialMode);
  }, [initialMode, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        overlayClassName="bg-background/55 backdrop-blur-md"
        className="max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-xl overflow-y-auto rounded-[2rem] border border-white/60 bg-background/95 p-6 shadow-2xl sm:p-8"
      >
        <DialogTitle className="sr-only">{mode === "login" ? "Sign in" : "Create account"}</DialogTitle>
        {mode === "login" ? (
          <LoginForm compact onSuccess={() => onOpenChange(false)} onSwitchToRegister={() => setMode("register")} />
        ) : (
          <RegisterForm compact onSuccess={() => onOpenChange(false)} onSwitchToLogin={() => setMode("login")} />
        )}
      </DialogContent>
    </Dialog>
  );
}
